import { useAuth } from '@cmg/auth';
import { Stack } from '@cmg/design-system';
import defaultTo from 'lodash/defaultTo';
import isNil from 'lodash/isNil';
import React, { useMemo } from 'react';

import { useCheckFinalInstitutionalAllocationAccess } from '../../../../../../common/util/check-access/useCheckFinalInstitutionalAllocationAccess';
import {
  AllocationAcknowledgement,
  IndicationStatus,
  InstitutionalIndicationOrderType,
} from '../../../../../../graphql';
import { isIndicationStatusAndTypeValid } from '../../../../../order-book/utils';
import { isInvestorContactRequired } from '../../../../utils/isInvestorContactRequired';
import { isReg144A } from '../../../compliance/context/hooks/certificates/Certificates.model';
import { useIndicationActivitySalesAndTradingContext } from '../../context/IndicationActivitySalesAndTradingContext';
import AllocationSection from '../allocation/AllocationSection';
import AllocationDetails from '../allocation/components/allocation-details/AllocationDetails';
import useSalesAndTradingAllocation from '../allocation/hooks/useSalesAndTradingAllocation';
import InsufficientPermissionsBanner from '../insufficient-permissions-banner/InsufficientPermissionsBanner';
import { useInvestorContactEditingState } from '../investor-contact-information/hooks/useInvestorContactEditingState';
import InvestorContactInformationForm from '../investor-contact-information/InvestorContactInformationForm';
import DuplicateIndicationBanner from './sales-and-trading/duplicate-indication-banner/DuplicateIndicationBanner';
import IndicationSectionCreateView from './sales-and-trading/indication-section-create-view/IndicationSectionCreateView';
import IndicationSectionEditView from './sales-and-trading/indication-section-edit-view/IndicationSectionEditView';
import IndicationSectionReadView from './sales-and-trading/indication-section-read-view/IndicationSectionReadView';

export type Props = {
  editable: boolean;
  isInvestorCoveredAndEditable?: boolean;
  isProspectusDocumentMissing?: boolean;
  isOrderBookOpen: boolean;
  displayMissingContacts: boolean;
  setDisplayMissingContacts: (val: boolean) => void;
  canUpdateCoveredIndication: boolean;
};

/**
 * Container component used for rendering the
 * Manage Covered Indication for ST user.
 */
const ManageCoveredIndication: React.FC<Props> = ({
  editable,
  isInvestorCoveredAndEditable,
  isProspectusDocumentMissing,
  isOrderBookOpen,
  displayMissingContacts,
  setDisplayMissingContacts,
  canUpdateCoveredIndication,
}) => {
  const { isEditingInvestorContacts, setIsEditingInvestorContacts } =
    useInvestorContactEditingState();
  const {
    complianceWarning: { hasComplianceWarning, canByPassComplianceWarning },
    coveredIndication,
    basicPublishedOffering,
    publishedOffering,
  } = useIndicationActivitySalesAndTradingContext();
  const { investorContactMandatory } = useAuth();

  const { canRead: canReadFinalInstitutionalAllocation } =
    useCheckFinalInstitutionalAllocationAccess({
      offeringId: basicPublishedOffering?.id ?? '',
    });

  const [isEditingIndication, setIsEditingIndication] = React.useState(false);

  const investorReplyStatus = defaultTo(
    coveredIndication?.investorReplyStatus,
    AllocationAcknowledgement.Unacknowledged
  );
  const isAllocationReleased = !isNil(coveredIndication?.allocationShares);
  const displayAllocationActions = useMemo(() => {
    if (!isAllocationReleased || coveredIndication.allocationShares == null) return false;
    return (
      canUpdateCoveredIndication &&
      coveredIndication &&
      isIndicationStatusAndTypeValid(coveredIndication.status, coveredIndication.type)
    );
  }, [canUpdateCoveredIndication, coveredIndication, isAllocationReleased]);

  const allocationSectionProps = useSalesAndTradingAllocation({
    offeringId: basicPublishedOffering?.id,
    indicationId: coveredIndication?.id,
    allocationVersion: defaultTo(coveredIndication?.allocationVersion, undefined),
    investorReplyStatus,
    billingAndDeliveryAgentCmgEntityKey: defaultTo(
      coveredIndication?.billingAndDeliveryAgentCmgEntityKey,
      null
    ),
  });

  const investorContactRequired = isInvestorContactRequired(
    investorContactMandatory,
    basicPublishedOffering?.type
  );

  const investorContactMandatoryValue = investorContactRequired && !isProspectusDocumentMissing;

  const isReg_144A = isReg144A(publishedOffering?.instruments?.[0]);

  const isCreatingIndication =
    basicPublishedOffering &&
    !coveredIndication &&
    (isReg_144A || !hasComplianceWarning || canByPassComplianceWarning) &&
    isOrderBookOpen;

  const shouldShowInsufficientPermissionsBanner =
    isCreatingIndication && !canUpdateCoveredIndication;

  return (
    <div aria-label="Manage Covered Indication">
      <Stack gap={1}>
        {coveredIndication?.status === IndicationStatus.Duplicate && <DuplicateIndicationBanner />}
      </Stack>

      {shouldShowInsufficientPermissionsBanner && <InsufficientPermissionsBanner />}

      {isCreatingIndication && !isEditingIndication && canUpdateCoveredIndication && (
        <IndicationSectionCreateView
          investorContactMandatory={!!investorContactMandatoryValue}
          isInvestorContactRequired={investorContactRequired}
          displayMissingContacts={displayMissingContacts}
        />
      )}
      {isEditingIndication && !isCreatingIndication && (
        <IndicationSectionEditView
          onFinishEditing={() => setIsEditingIndication(false)}
          displayMissingContacts={displayMissingContacts}
          isInvestorContactRequired={investorContactRequired}
        />
      )}
      {!isEditingIndication && !isCreatingIndication && coveredIndication && (
        <IndicationSectionReadView
          onStartEditing={() => setIsEditingIndication(true)}
          isIndicationEditable={editable}
          displayMissingContacts={displayMissingContacts}
          startEditingInvestorContacts={() => setIsEditingInvestorContacts(true)}
        />
      )}

      {basicPublishedOffering && coveredIndication && (
        <InvestorContactInformationForm
          investorContactMandatory={investorContactMandatoryValue}
          offering={basicPublishedOffering}
          indication={coveredIndication}
          isCoveredAccount
          setDisplayMissingContacts={setDisplayMissingContacts}
          isInvestorCoveredAndEditable={isInvestorCoveredAndEditable && canUpdateCoveredIndication}
          isEditingInvestorContacts={isEditingInvestorContacts}
          setIsEditingInvestorContacts={setIsEditingInvestorContacts}
          isInvestorContactRequired={investorContactRequired}
        />
      )}

      {canReadFinalInstitutionalAllocation &&
        basicPublishedOffering &&
        coveredIndication &&
        coveredIndication.type !== InstitutionalIndicationOrderType.Pass && (
          <AllocationSection
            sectionTitle="Final Allocation"
            isReleased={isAllocationReleased}
            displayAllocationActions={displayAllocationActions}
            {...allocationSectionProps}
          >
            <AllocationDetails
              offeringId={basicPublishedOffering.id}
              isReleased={isAllocationReleased}
              allocationData={{
                offerPrice: basicPublishedOffering.offerPrice!,
                shareQuantity: defaultTo(coveredIndication.allocationShares, null),
                pricingCurrencyCode: basicPublishedOffering.pricingCurrencyCode,
                orderType: coveredIndication.type,
                indicationStatus: coveredIndication.status,
                isOrderBookOpen,
                investorReplyStatus,
              }}
            />
          </AllocationSection>
        )}
    </div>
  );
};

export default ManageCoveredIndication;
